import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography,
} from '@mui/material'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { supabase } from '../../supabaseClient'

// Threaded mentor↔student messages tied to a project. Both sides
// see the same chronological list. Reply input shown only when
// `editable` is true (i.e. the underlying submission is still
// awaiting a mentor decision).
//
// Data lives in mentor_review_log under the new 'message' /
// 'student_message' actions; existing RLS already covers reads
// for both sides. Sends route through the submission-message
// edge function which decides direction by caller role.

interface MessageEntry {
  log_id:        string
  action:        'message' | 'student_message'
  note:          string | null
  acted_at:      string
  reviewer_id:   string | null
  reviewer_name: string | null
  is_student:    boolean
}

interface Props {
  projectId: string
  /** When false, the thread renders read-only (decision recorded). */
  editable:  boolean
  /** Caller role lens — affects which side reads as "you" in the UI. */
  viewerSide: 'student' | 'staff'
}

function fmtTime(iso: string) {
  const d  = new Date(iso)
  const ms = Date.now() - d.getTime()
  const m  = Math.floor(ms / 60_000)
  if (m < 1)  return 'just now'
  if (m < 60) return `${m}m ago`
  const h  = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function ConversationThread({ projectId, editable, viewerSide }: Props) {
  const [messages, setMessages] = useState<MessageEntry[]>([])
  const [loading,  setLoading]  = useState(true)
  const [sending,  setSending]  = useState(false)
  const [body,     setBody]     = useState('')
  const [error,    setError]    = useState<string | null>(null)
  const [ccParent, setCcParent] = useState(false)
  const [polishing, setPolishing] = useState(false)

  const isStaff = viewerSide === 'staff'

  async function load() {
    setLoading(true); setError(null)
    try {
      const { data: rows, error: rErr } = await supabase
        .from('mentor_review_log')
        .select('log_id, action, note, acted_at, reviewer_id')
        .eq('project_id', projectId)
        .in('action', ['message', 'student_message'])
        .order('acted_at', { ascending: true })
      if (rErr) throw new Error(rErr.message)

      const ids = Array.from(new Set((rows ?? []).map((r: any) => r.reviewer_id).filter(Boolean) as string[]))
      const { data: users } = ids.length > 0
        ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', ids)
        : { data: [] as { user_id: string; first_name: string; last_name: string }[] }
      const userMap = Object.fromEntries(
        (users ?? []).map(u => [u.user_id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || null])
      )

      setMessages(((rows ?? []) as any[]).map(r => ({
        log_id:        r.log_id,
        action:        r.action,
        note:          r.note,
        acted_at:      r.acted_at,
        reviewer_id:   r.reviewer_id,
        reviewer_name: r.reviewer_id ? (userMap[r.reviewer_id] ?? null) : null,
        is_student:    r.action === 'student_message',
      })))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectId])

  async function send() {
    const trimmed = body.trim()
    if (sending || trimmed.length === 0) return
    setSending(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('submission-message', {
        body: { project_id: projectId, body: trimmed, cc_parent: isStaff && ccParent },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      setBody('')
      setCcParent(false)
      await load()
    } catch (e: any) {
      setError(e.message ?? 'Could not send — please try again.')
    } finally {
      setSending(false)
    }
  }

  async function polish() {
    const trimmed = body.trim()
    if (polishing || sending || trimmed.length === 0) return
    setPolishing(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('polish-message', {
        body: { text: trimmed },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      if (data?.polished) setBody(data.polished)
    } catch (e: any) {
      setError(e.message ?? 'Could not polish — please try again.')
    } finally {
      setPolishing(false)
    }
  }

  if (loading) return <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={18} /></Box>

  const empty = messages.length === 0

  return (
    <Box>
      {empty && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: editable ? 1.5 : 0 }}>
          {editable
            ? 'No messages yet. Use this space to ask a clarifying question or share context while the submission is under review.'
            : 'No messages were exchanged on this submission.'}
        </Typography>
      )}

      {messages.map(m => {
        const mine = (viewerSide === 'student' && m.is_student)
                  || (viewerSide === 'staff'   && !m.is_student)
        const speakerLabel = m.is_student
          ? (viewerSide === 'student' ? 'You' : (m.reviewer_name ?? 'Student'))
          : (viewerSide === 'staff'   ? 'You' : (m.reviewer_name ?? 'Mentor'))

        return (
          <Box
            key={m.log_id}
            sx={{
              display:        'flex',
              justifyContent: mine ? 'flex-end' : 'flex-start',
              mb:             1.25,
            }}
          >
            <Box
              sx={{
                maxWidth:    '80%',
                p:           1.5,
                borderRadius: 1.5,
                bgcolor:     mine ? 'rgba(15,43,76,0.08)' : 'background.paper',
                border:      '1px solid',
                borderColor: mine ? 'primary.light' : 'divider',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
                {speakerLabel} · {fmtTime(m.acted_at)}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, mt: 0.5 }}>
                {m.note}
              </Typography>
            </Box>
          </Box>
        )
      })}

      {editable && (
        <Box sx={{ mt: empty ? 0 : 2 }}>
          <TextField
            fullWidth multiline rows={2} size="small"
            placeholder={viewerSide === 'staff'
              ? 'Ask the student a clarifying question, share context, etc.'
              : 'Ask your mentor a question or share context.'}
            value={body}
            onChange={e => setBody(e.target.value)}
            disabled={sending}
            sx={{ mb: 1 }}
          />
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {isStaff && (
              <FormControlLabel
                sx={{ mr: 'auto' }}
                control={
                  <Checkbox
                    size="small"
                    checked={ccParent}
                    onChange={e => setCcParent(e.target.checked)}
                    disabled={sending}
                  />
                }
                label={<Typography variant="body2" color="text.secondary">CC parent by email</Typography>}
              />
            )}
            {isStaff && (
              <Button
                size="small" variant="text" startIcon={polishing ? <CircularProgress size={14} /> : <AutoFixHighIcon fontSize="small" />}
                onClick={polish}
                disabled={polishing || sending || body.trim().length === 0}
                sx={{ ml: isStaff ? 0 : 'auto' }}
              >
                {polishing ? 'Polishing…' : 'Polish with AI'}
              </Button>
            )}
            <Button
              size="small" variant="contained"
              onClick={send}
              disabled={sending || polishing || body.trim().length === 0}
              sx={{ ml: isStaff ? 0 : 'auto' }}
            >
              {sending ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Sending…</> : 'Send'}
            </Button>
          </Box>
        </Box>
      )}

      {!editable && messages.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
          This conversation is closed (the mentor recorded a decision). New questions can be raised when there's an open submission.
        </Typography>
      )}
    </Box>
  )
}
